import React, { useState, useEffect, useContext } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AuthContext from '../../contexts/AuthContext';
import { requestNotificationPermission } from '../../services/browserNotificationService';
import { useTranslation } from 'react-i18next';

const NotificationSettings: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const [browserNotifications, setBrowserNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [postNotifications, setPostNotifications] = useState(true);
  const [likeNotifications, setLikeNotifications] = useState(true);
  const [commentNotifications, setCommentNotifications] = useState(true);
  const [followNotifications, setFollowNotifications] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const { t } = useTranslation();

  // Cargar configuración actual del usuario
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentUser) return;

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Establecer valores predeterminados si no existen
          setBrowserNotifications(userData.browserNotifications !== false);
          setEmailNotifications(userData.emailNotifications !== false);
          setPostNotifications(userData.postNotifications !== false);
          setLikeNotifications(userData.likeNotifications !== false);
          setCommentNotifications(userData.commentNotifications !== false);
          setFollowNotifications(userData.followNotifications !== false);
        }
      } catch (error) {
        console.error('Error al cargar configuración de notificaciones:', error);
      }
    };

    loadSettings();
  }, [currentUser]);

  // Guardar configuración
  const handleSaveSettings = async () => {
    if (!currentUser) return;

    setIsSaving(true);
    setSaveMessage('');

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userDocRef, {
        browserNotifications,
        emailNotifications,
        postNotifications,
        likeNotifications,
        commentNotifications,
        followNotifications,
        updatedAt: new Date()
      });

      // Si las notificaciones del navegador están habilitadas, solicitar permiso
      if (browserNotifications) {
        try {
          const granted = await requestNotificationPermission(currentUser.uid);
          if (!granted) {
            setSaveMessage('Configuración guardada, pero no se concedió permiso para notificaciones del navegador.');
            return;
          }
        } catch (error) {
          console.error('Error al solicitar permiso de notificaciones:', error);
          setSaveMessage('Configuración guardada, pero hubo un error al configurar las notificaciones del navegador.');
          return;
        }
      }

      setSaveMessage('Configuración de notificaciones guardada correctamente.');
    } catch (error) {
      console.error('Error al guardar configuración de notificaciones:', error);
      setSaveMessage('Error al guardar la configuración. Inténtalo de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  // Si no hay usuario autenticado, no mostrar nada
  if (!currentUser) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-4">{t('settings.notificationSettings')}</h2>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">{t('settings.browserNotifications')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('settings.browserNotificationsDescription')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={browserNotifications}
              onChange={(e) => setBrowserNotifications(e.target.checked)}
              disabled={isSaving}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-domired-300 dark:peer-focus:ring-domired-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-domired-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">{t('settings.emailNotifications')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('settings.emailNotificationsDescription')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              className="sr-only peer" 
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
              disabled={isSaving}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-domired-300 dark:peer-focus:ring-domired-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-domired-600"></div>
          </label>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="font-medium mb-2">{t('settings.notificationTypes')}</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span>{t('settings.newPostsNotifications')}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={postNotifications}
                  onChange={(e) => setPostNotifications(e.target.checked)}
                  disabled={isSaving}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-domired-300 dark:peer-focus:ring-domired-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-domired-600"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <span>{t('settings.likesNotifications')}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={likeNotifications}
                  onChange={(e) => setLikeNotifications(e.target.checked)}
                  disabled={isSaving}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-domired-300 dark:peer-focus:ring-domired-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-domired-600"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <span>{t('settings.commentsNotifications')}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={commentNotifications}
                  onChange={(e) => setCommentNotifications(e.target.checked)}
                  disabled={isSaving}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-domired-300 dark:peer-focus:ring-domired-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-domired-600"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <span>{t('settings.followersNotifications')}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={followNotifications}
                  onChange={(e) => setFollowNotifications(e.target.checked)}
                  disabled={isSaving}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-domired-300 dark:peer-focus:ring-domired-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-domired-600"></div>
              </label>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end mt-6">
          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50"
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </button>
        </div>
        
        {saveMessage && (
          <div className={`mt-4 p-3 rounded-md ${saveMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {saveMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
